import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/shared";
import { Plus, ListChecks, Pencil, Trash2 } from "lucide-react";

export default function ActivityTypes() {
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", km_margin: 0 });
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/activity-types").then(({ data }) => setTypes(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("Il nome è obbligatorio"); return; }
    try {
      const payload = { name: form.name, description: form.description, km_margin: parseInt(form.km_margin, 10) || 0 };
      if (editId) await api.put(`/activity-types/${editId}`, payload);
      else await api.post("/activity-types", payload);
      toast.success(editId ? "Attività aggiornata" : "Attività creata");
      setOpen(false); setForm({ name: "", description: "", km_margin: 0 }); setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminare il tipo di attività "${t.name}"?`)) return;
    try { await api.delete(`/activity-types/${t.id}`); toast.success("Attività eliminata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Anagrafiche" title="Attività & Scadenze" subtitle="Definisci i tipi di attività: revisione, assicurazione, manutenzione, taratura…">
        <Button onClick={() => { setForm({ name: "", description: "" }); setEditId(null); setOpen(true); }} data-testid="new-activity-button">
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo tipo
        </Button>
      </PageHeader>

      {types.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {types.map((t) => (
            <Card key={t.id} data-testid={`activity-${t.id}`} className="p-5 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                  <ListChecks className="h-5 w-5 text-primary" />
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setForm({ name: t.name, description: t.description, km_margin: t.km_margin || 0 }); setEditId(t.id); setOpen(true); }} data-testid={`edit-activity-${t.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`delete-activity-${t.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
              <div className="font-heading font-bold text-lg mt-3">{t.name}</div>
              {t.description && <p className="text-sm text-muted-foreground mt-1">{t.description}</p>}
              <div className="flex items-center gap-2 mt-3">
                <span className="label-caps text-muted-foreground">{t.usage_count} registrazioni</span>
                {t.km_margin > 0 && (
                  <span className="label-caps text-primary bg-primary/10 px-2 py-0.5 rounded" data-testid={`activity-margin-${t.id}`}>
                    Preavviso {t.km_margin} km/ore
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="rounded-lg"><EmptyState icon={ListChecks} title="Nessun tipo di attività" subtitle="Crea il primo tipo di scadenza (es. Revisione)." /></Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editId ? "Modifica attività" : "Nuovo tipo di attività"}</DialogTitle>
            <DialogDescription>Definisci un tipo di attività/scadenza per i beni.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input data-testid="activity-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Revisione, Assicurazione, Taratura" />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div className="space-y-1.5">
              <Label>Margine di preavviso km/ore</Label>
              <Input type="number" min="0" data-testid="activity-km-margin-input" value={form.km_margin}
                onChange={(e) => setForm({ ...form, km_margin: e.target.value })}
                placeholder="Es. 1000 (km) o 50 (ore)" />
              <p className="text-xs text-muted-foreground">Avvisa quando mancano questi km/ore alla soglia. Lascia 0 per usare il valore predefinito (1000 km / 50 ore).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-activity-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
